import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import LandingArticle from '@/components/LandingArticle';

const UPDATED = '2026-10-21';

const InlineCTA = () => (
  <div className="my-6 not-prose rounded-2xl border border-emerald-700/15 bg-emerald-50/60 p-5 flex flex-col sm:flex-row sm:items-center gap-4 justify-between" data-testid="inline-cta">
    <div className="text-sm text-emerald-900">
      <strong>Prefer a ready-made clean-label choice?</strong>
      <span className="block text-emerald-800/80 mt-0.5">IIY Phytoventra BCM-95® ticks every box below — piperine-free, vegan, 650 mg curcumin/serving. £44.99 (first bottle £37.99), UK VAT &amp; delivery included.</span>
    </div>
    <Link to="/products/94056720-f372-4eef-bcfe-74063137a6e5" className="inline-flex shrink-0 items-center gap-1.5 bg-emerald-700 text-white font-bold py-2.5 px-4 rounded-full text-sm hover:bg-emerald-800 transition-colors" data-testid="hub-inline-cta">
      Shop BCM-95® <ArrowRight className="w-3.5 h-3.5" />
    </Link>
  </div>
);

const GuideLink = ({ to, children }) => (
  <Link to={to} className="text-emerald-700 font-semibold underline decoration-emerald-300 underline-offset-2 hover:decoration-emerald-600">{children}</Link>
);

const sections = [
  {
    id: 'quick-answer',
    heading: 'How to choose a turmeric supplement: 5 quick checks',
    body: (
      <>
        <p>The best way to compare turmeric supplements is to ignore the big front-of-pack number and check five things: the <strong>curcumin content per serving</strong>, the <strong>named extract</strong>, <strong>how absorption is achieved</strong>, the <strong>clean-label &amp; allergen profile</strong>, and the <strong>UK consumer protections</strong> behind the seller.</p>
        <InlineCTA />
      </>
    ),
  },
  {
    id: 'curcumin-content',
    heading: '1. Curcumin content — read the serving, not the spice weight',
    body: (
      <>
        <p>Raw turmeric is only about 2–5% curcuminoids, so &ldquo;Turmeric 1000&nbsp;mg&rdquo; tells you little on its own. Look for the <strong>curcumin (or curcuminoid) content per serving</strong>. As a reference point, IIY Phytoventra BCM-95<sup>&reg;</sup> states <strong>650&nbsp;mg of curcumin per 2-capsule serving</strong>, from 1000&nbsp;mg of purified turmeric extract.</p>
        <p>For the turmeric-vs-curcumin basics, see <GuideLink to="/curcumin-vs-turmeric">Curcumin vs Turmeric</GuideLink>.</p>
      </>
    ),
  },
  {
    id: 'absorption',
    heading: '2. Absorption — curcumin is poorly absorbed on its own',
    body: (
      <>
        <p>Plain curcumin is absorbed and excreted quickly, so every serious product uses an absorption strategy — turmeric essential oils (BCM-95<sup>&reg;</sup>), black pepper (piperine), phospholipid complexes or colloidal systems. The label should tell you <em>which</em>. BCM-95<sup>&reg;</sup> uses turmeric&rsquo;s own oils and reports up to <strong>7× greater absorption than standard curcumin</strong> (Antony et al., 2008) — with no piperine.</p>
        <p>Prefer no pepper? See <GuideLink to="/turmeric-without-black-pepper">Turmeric Without Black Pepper</GuideLink>. Full comparison: <GuideLink to="/best-curcumin-supplement-uk">Best Curcumin Supplement UK</GuideLink>.</p>
      </>
    ),
  },
  {
    id: 'clean-label',
    heading: '3. Clean label, allergens & vegan status',
    body: (
      <>
        <p>Check the capsule (gelatin vs vegan cellulose), the allergen declaration, and whether there are unnecessary carriers. A clean-label product keeps excipients to a minimum. More detail in <GuideLink to="/vegan-turmeric-supplement-uk">Vegan Turmeric Supplement UK</GuideLink>.</p>
      </>
    ),
  },
  {
    id: 'uk-protections',
    heading: '4 & 5. Named extract + UK consumer protections',
    body: (
      <>
        <ul className="not-prose space-y-2.5 my-2">
          {[
            'A named, studied extract (BCM-95®/Curcugreen®, Meriva®, Longvida®) with published pharmacokinetic data.',
            'A UK-registered food business with a real address and a VAT invoice.',
            'A clear returns policy (14 days for unopened goods under the UK Consumer Contracts Regulations).',
            'Transparent, all-inclusive pricing — no surprise VAT or delivery at checkout.',
          ].map((t) => (
            <li key={t} className="flex items-start gap-2.5 text-slate-700">
              <Check className="w-4 h-4 mt-1 shrink-0 text-emerald-600" />
              <span>{t}</span>
            </li>
          ))}
        </ul>
      </>
    ),
  },
  {
    id: 'how-much',
    heading: 'How much curcumin should a daily serving provide?',
    body: (
      <>
        <p>There is no official UK recommended intake for curcumin as it is a food supplement, so always follow the directions on the pack. Many standardised-curcumin products provide somewhere in the region of a few hundred milligrams to around a gram of curcumin per day. IIY Phytoventra BCM-95<sup>&reg;</sup>&rsquo;s directions are <strong>2 capsules a day (650&nbsp;mg curcumin), taken with food</strong>. If you are pregnant, breastfeeding, taking medication or under medical supervision, check with a healthcare professional first.</p>
      </>
    ),
  },
  {
    id: 'faq',
    heading: 'Choosing a turmeric supplement — frequently asked questions',
    body: (
      <>
        <p className="font-semibold text-emerald-900">How do I choose a good turmeric supplement?</p>
        <p>Check the curcumin content per serving (not the total turmeric weight), whether it uses a named studied extract, how absorption is achieved, the vegan/allergen profile, and whether it is sold by a UK business with a VAT receipt and clear returns.</p>
        <p className="font-semibold text-emerald-900 mt-4">How much curcumin is in a daily serving?</p>
        <p>It varies by product; there is no official UK recommended amount. As a reference, BCM-95<sup>&reg;</sup> provides 650&nbsp;mg of curcumin in its 2-capsule daily serving. Always follow the pack directions.</p>
        <p className="font-semibold text-emerald-900 mt-4">Is a higher turmeric number always better?</p>
        <p>No. A big &ldquo;turmeric&rdquo; number can still contain very little curcumin and may not be well absorbed. Curcumin content per serving and the absorption method matter far more than the headline spice weight.</p>
      </>
    ),
  },
];

const faqLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    { q: 'How do I choose a good turmeric supplement?', a: 'Check the curcumin content per serving (not the total turmeric weight), whether it uses a named studied extract, how absorption is achieved, the vegan/allergen profile, and whether it is sold by a UK business with a VAT receipt and clear returns.' },
    { q: 'How much curcumin is in a daily serving?', a: 'It varies by product; there is no official UK recommended amount. As a reference, BCM-95® provides 650 mg of curcumin in its 2-capsule daily serving. Always follow the pack directions.' },
    { q: 'Is a higher turmeric number always better?', a: 'No. A big "turmeric" number can still contain very little curcumin and may not be well absorbed. Curcumin content per serving and the absorption method matter far more than the headline spice weight.' },
  ].map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
};

export default function HowToChooseTurmericSupplement() {
  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqLd) }} />
      <LandingArticle
        title="How to Choose a Turmeric Supplement (UK Guide) | IIY"
        description="A 5-point UK checklist for choosing a turmeric or curcumin supplement — curcumin per serving, absorption method, vegan & allergen checks, how much per day, and UK buyer protections."
        keywords="how to choose a turmeric supplement, how much curcumin per day, curcumin dosage UK, what to look for turmeric supplement, best curcumin supplement, turmeric supplement UK, BCM-95"
        pathOverride="/how-to-choose-a-turmeric-supplement"
        heading="How to Choose a Turmeric Supplement: A UK Checklist"
        subheading="Five checks that matter more than the front-of-pack number — curcumin per serving, absorption, clean label, how much per day, and the UK protections behind the seller."
        tag="Buyer's Checklist"
        datePublished={UPDATED}
        dateModified={UPDATED}
        sections={sections}
      />
    </>
  );
}
